import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AUTOMATIC_SUPPORTED_UNITS_SCHEMA } from '@schuettflix/interfaces';
import { toAutomaticUnit } from '../utils/toAutomaticUnit.ts';
import { AutomaticProductsFormValues } from '../schema/getAutomaticProductsFormSchema.ts';
import { useAutomaticFormContext } from '../context/AutomaticFormContext.tsx';
import { useOrderFormQuantities } from '../hooks/useOrderFormQuantities.ts';
import { Fieldset, FieldsetBody, FieldsetColumn, FieldsetLabel } from '../components/Fieldset.tsx';
import { NegativeSalesPriceHint, ZeroSalesPriceHint } from '@/shared/components/PriceInputHints.tsx';
import { PriceInput } from '@/shared/inputs/PriceInput.tsx';
import { TaxClassInput } from '@/shared/inputs/TaxClassInput.tsx';
import { UnitInput } from '@/shared/inputs/UnitInput.tsx';

export function OrderSalesTransportFieldset() {
    const { t } = useTranslation();
    const { taxClassOptions, customerOrganization, formRules } = useAutomaticFormContext();
    const methods = useFormContext<AutomaticProductsFormValues>();
    const { register, watch, setValue } = methods;
    const { onSalesTransportUnitChange } = useOrderFormQuantities(methods);

    const [salesTransportPrice, salesTransportUnit, currencyCode, showZeroSalesTransportPrice] = watch([
        'salesTransportPrice',
        'salesTransportUnit',
        'currencyCode',
        'showZeroSalesTransportPrice',
    ]);

    return (
        <Fieldset title={t('forms.automaticProductsForm.sections.transport')} dataTest="order-sales-transport-fieldset">
            <FieldsetBody className="@sm:grid-cols-3 @2xl:grid-cols-8 grid-cols-1">
                <FieldsetColumn className="@2xl:col-span-4">
                    <FieldsetLabel>{t('forms.automaticProductsForm.labels.unitPrice')}</FieldsetLabel>
                    <PriceInput
                        {...register('salesTransportPrice')}
                        unit={salesTransportUnit}
                        currencyCode={currencyCode}
                        testId="sales-transport-price-input"
                    />
                </FieldsetColumn>
                <FieldsetColumn className="@2xl:col-span-2">
                    <FieldsetLabel>{t('forms.automaticProductsForm.labels.unit')}</FieldsetLabel>
                    <UnitInput
                        {...register('salesTransportUnit')}
                        options={[...AUTOMATIC_SUPPORTED_UNITS_SCHEMA.options]}
                        disabled={formRules?.salesTransportUnit?.disabled}
                        onUnitChange={unit => onSalesTransportUnitChange(toAutomaticUnit(unit))}
                    />
                </FieldsetColumn>
                <FieldsetColumn className="@2xl:col-span-2">
                    <FieldsetLabel>{t('forms.automaticProductsForm.labels.taxClass')}</FieldsetLabel>
                    <TaxClassInput
                        {...register('salesTransportTaxClassId')}
                        options={taxClassOptions}
                        testId="sales-transport-tax-class-input"
                        disabled={formRules?.salesTransportTaxClassId?.disabled}
                    />
                </FieldsetColumn>

                {salesTransportPrice === 0 && (
                    <div className="@2xl:col-span-8 mt-3">
                        <ZeroSalesPriceHint
                            orderingOrganizationName={customerOrganization.name}
                            isBillingRelevant={showZeroSalesTransportPrice ?? false}
                            onClick={() =>
                                setValue('showZeroSalesTransportPrice', !(showZeroSalesTransportPrice ?? false))
                            }
                        />
                    </div>
                )}
                {Number(salesTransportPrice) < 0 && (
                    <div className="@2xl:col-span-8 mt-3">
                        <NegativeSalesPriceHint orderingOrganizationName={customerOrganization.name} />
                    </div>
                )}
            </FieldsetBody>
        </Fieldset>
    );
}
